import React from 'react';
import { Box, Stack, Typography, Chip, useTheme, alpha } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { CircularProgressIndicator, MetricInfoIcon } from '../../../../../shared/components/charts';
import { DashboardCard, DashboardCardSkeleton } from '../../../../../shared/components';
import { formatTrendPercent, getRagColor, getStrainBand } from '../../../../../shared/utils';
import { useMetricsStats } from '../../../../../shared/hooks';
import { roundToDecimal } from '../../shared/utils';
import type { MetricsFilters } from '../../../../../shared/api/types';

type GaugeMetric = 'recovery' | 'strain' | 'sleep';

interface WellnessScoreGaugeProps {
  metric: GaugeMetric;
  filterParams: MetricsFilters;
}

const METRIC_CONFIG: Record<GaugeMetric, { metrics: string[]; label: string; target?: string }> = {
  recovery: { metrics: ['recovery_score', 'recovery'], label: 'Recovery', target: 'Good ≥ 65%' },
  strain: { metrics: ['strain'], label: 'Strain' },
  sleep: { metrics: ['sleep_performance'], label: 'Sleep', target: 'Good ≥ 75%' },
};

export const WellnessScoreGauge: React.FC<WellnessScoreGaugeProps> = ({ metric, filterParams }) => {
  const theme = useTheme();
  const config = METRIC_CONFIG[metric];

  const { data: statsData, isLoading } = useMetricsStats({ ...filterParams, metrics: config.metrics });

  if (isLoading) {
    return <DashboardCardSkeleton height={200} />;
  }

  const resolveRagColor = (value: number, apiRagColor: string | undefined): string | undefined => {
    if (apiRagColor) return getRagColor(apiRagColor, theme);
    return undefined;
  };

  const renderTrendBadge = (change: string | undefined) => {
    const normalized = formatTrendPercent(change) ?? '';
    if (!normalized) return null;
    const isNeg = normalized.startsWith('-');
    const isPos = normalized.startsWith('+');
    const color = isNeg ? theme.palette.error.main : isPos ? theme.palette.success.main : theme.palette.text.secondary;
    const Icon = isNeg ? TrendingDownIcon : isPos ? TrendingUpIcon : TrendingFlatIcon;
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color }}>
        <Typography component='span' sx={{ fontWeight: 600, fontSize: '0.8rem', color }}>
          {normalized}
        </Typography>
        <Icon sx={{ fontSize: '0.9rem', color }} />
      </Box>
    );
  };

  let gaugeNode: React.ReactNode = null;

  if (metric === 'recovery') {
    const stat = statsData?.data?.recovery ?? statsData?.data?.recovery_score;
    const value = roundToDecimal(stat?.value || 0);
    const color = resolveRagColor(value, stat?.rag_color);
    gaugeNode = (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <CircularProgressIndicator
          value={value}
          label={config.label}
          icon={<PersonIcon fontSize='small' />}
          color={color}
          unit={stat?.unit || '%'}
        />
        {renderTrendBadge(stat?.change)}
        <Stack spacing={0.5} alignItems='center'>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {stat?.rag_label && color && (
              <Chip
                label={stat.rag_label}
                size='small'
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, color, backgroundColor: alpha(color, 0.12) }}
              />
            )}
            <MetricInfoIcon metricKey='recovery_score' />
          </Box>
          {config.target && (
            <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
              {config.target}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  } else if (metric === 'strain') {
    const stat = statsData?.data?.strain;
    const value = roundToDecimal(stat?.value || 0);
    const band = value > 0 ? getStrainBand(value, theme) : { label: 'Monitor', color: getRagColor('red', theme) };
    gaugeNode = (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <CircularProgressIndicator
          value={value}
          label={config.label}
          icon={<DirectionsRunIcon fontSize='small' />}
          color={band.color}
          max={roundToDecimal((stat?.additional_fields?.max as number | undefined) || 21)}
        />
        {renderTrendBadge(stat?.change)}
        <Stack spacing={0.5} alignItems='center'>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {band.label && band.color && (
              <Chip
                label={band.label}
                size='small'
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: band.color,
                  backgroundColor: alpha(band.color, 0.12),
                }}
              />
            )}
            <MetricInfoIcon metricKey='strain' />
          </Box>
        </Stack>
      </Box>
    );
  } else {
    const stat = statsData?.data?.sleep_performance;
    const value = roundToDecimal(stat?.value || 0);
    const color = resolveRagColor(value, stat?.rag_color);
    gaugeNode = (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <CircularProgressIndicator
          value={value}
          label={config.label}
          icon={<BedtimeIcon fontSize='small' />}
          color={color}
          unit={stat?.unit || '%'}
        />
        {renderTrendBadge(stat?.change)}
        <Stack spacing={0.5} alignItems='center'>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {stat?.rag_label && color && (
              <Chip
                label={stat.rag_label}
                size='small'
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, color, backgroundColor: alpha(color, 0.12) }}
              />
            )}
            <MetricInfoIcon metricKey='sleep_performance' />
          </Box>
          {config.target && (
            <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
              {config.target}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <DashboardCard
      title={`${config.label} Wellness Overview Score`}
      subtitle={`Current ${config.label.toLowerCase()} score with trend`}
      expandable={false}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>{gaugeNode}</Box>
    </DashboardCard>
  );
};
